import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import { ArrowLeft, ArrowRight, Search, CheckCircle, Loader2 } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

interface LanguageStepProps {
  onNext: (data: { languages: string[] }) => void;
  onBack: () => void;
  initialData?: {
    languages?: string[];
    siteId?: string;
  };
}

const popularLanguages = ['es', 'fr', 'de', 'zh', 'ja', 'pt', 'it', 'ru', 'ar', 'hi'];

const LanguageStep: React.FC<LanguageStepProps> = ({
  onNext,
  onBack,
  initialData,
}) => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    initialData?.languages || []
  );
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      const response = await apiService.get('/languages');
      setLanguages(response.data || []);
    } catch (error) {
      console.error('Failed to load languages:', error);
    }
  };

  const toggleLanguage = (code: string) => {
    setSelectedLanguages((prev) =>
      prev.includes(code)
        ? prev.filter((l) => l !== code)
        : [...prev, code]
    );
  };

  const handleContinue = async () => {
    if (selectedLanguages.length === 0) {
      return;
    }

    try {
      setLoading(true);

      if (initialData?.siteId) {
        await apiService.put(`/sites/${initialData.siteId}`, {
          enabledLanguages: ['en', ...selectedLanguages],
        });
      }

      onNext({ languages: selectedLanguages });
    } catch (error) {
      console.error('Failed to update languages:', error);
    } finally {
      setLoading(false);
    }
  };

  const popularLangs = languages.filter((lang) =>
    popularLanguages.includes(lang.code)
  );

  const filteredLanguages = searchQuery
    ? languages.filter(
        (lang) =>
          lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lang.code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : languages;

  return (
    <CardContent className="p-12">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full text-3xl mb-4">
            🗣️
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Choose Your Target Languages
          </h2>
          <p className="text-muted-foreground">
            Select which languages you want to translate your content into
          </p>
        </div>

        {/* Selected Count */}
        <div className="mb-6">
          <div className="bg-primary/5 rounded-lg p-4 flex items-center justify-between">
            <span className="text-foreground font-medium">
              {selectedLanguages.length === 0
                ? 'No languages selected'
                : `${selectedLanguages.length} language${
                    selectedLanguages.length === 1 ? '' : 's'
                  } selected`}
            </span>
            {selectedLanguages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLanguages([])}
                className="text-primary"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Popular Languages */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Popular Languages
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {popularLangs.map((lang) => (
              <button
                key={lang.code}
                onClick={() => toggleLanguage(lang.code)}
                className={cn(
                  "p-4 rounded-lg border-2 text-left transition-all",
                  selectedLanguages.includes(lang.code)
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-foreground">{lang.name}</div>
                    <div className="text-sm text-muted-foreground">{lang.nativeName}</div>
                  </div>
                  {selectedLanguages.includes(lang.code) && (
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Search All Languages */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            All Languages
          </h3>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search languages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border rounded-lg">
            <div className="divide-y">
              {filteredLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => toggleLanguage(lang.code)}
                  className={cn(
                    "w-full px-4 py-3 text-left hover:bg-muted/50 transition",
                    selectedLanguages.includes(lang.code) && "bg-primary/5"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-foreground">{lang.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {lang.nativeName} ({lang.code})
                      </div>
                    </div>
                    {selectedLanguages.includes(lang.code) && (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Button
            onClick={handleContinue}
            disabled={selectedLanguages.length === 0 || loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </CardContent>
  );
};

export default LanguageStep;
